import axios from 'axios';
import router from '@/router';
import { parseToken } from '@/auth/utils';
import notifications from '@/utils/notifications';
import { auth as authConstants } from '@/store/constants';
import roles from 'simulix-shared/auth/roles';
import { translateApiError } from '@/utils/apiError';

const { UNSET_TOKEN_DATA, SET_TOKEN_DATA, SET_TEMP_TOKEN } = authConstants.mutations;

const initialState = {
  accessToken: null,
  tempToken: null,
};

const authGetters = {
  accessToken: (state) => state.accessToken || state.tempToken,
  isAccessTokenRefreshable: (state, getters) => getters.user && getters.user.refreshToken,
  accessTokenExp: (state, getters) => getters.user && getters.user.exp,
  user: (state) => state.accessToken && parseToken(state.accessToken),
  isUserAdmin: (state, getters) => getters.user && getters.user.role === roles.ADMIN,
};

const actions = {
  async initialAutoLogIn({ commit, getters, dispatch }) {
    if (getters.user) return;

    dispatch('setLoaderVisiblity', { isVisible: true, content: 'Logging user in...' });

    try {
      const accessToken = localStorage.getItem('simulix_bearer_token');
      if (accessToken) {
        commit(SET_TOKEN_DATA, accessToken);
        await dispatch('refreshAccessToken');
        dispatch('redirectToDefaultPage');
      } else {
        commit(UNSET_TOKEN_DATA);
        dispatch('redirectToLoginPage');
      }
    } catch (err) {
      dispatch('setErrorPageVisiblity', {
        isVisible: true,
        title: 'Log in error.',
        content: 'It was not possible to log in the user. Please try again later.',
      });
    }

    dispatch('setLoaderVisiblity', { isVisible: false });
  },
  async logIn({ commit, dispatch }, credentials) {
    try {
      const response = await axios.post('auth', credentials);
      const token = response.data.access_token;
      localStorage.setItem('simulix_bearer_token', token);
      commit(SET_TOKEN_DATA, token);
      dispatch('redirectToDefaultPage');
    } catch (err) {
      notifications.$emit('error', translateApiError(err, 'auth.loginFailed'));
      localStorage.removeItem('simulix_bearer_token');
      commit(UNSET_TOKEN_DATA);
      dispatch('redirectToLoginPage');
    }
  },
  async logOut({ commit, getters, dispatch }) {
    if (getters.isAccessTokenRefreshable) {
      try {
        await axios.delete('auth/', { data: { refresh_token: getters.user.refreshToken } });
      } catch (err) {
        /* Simply let it continue and log the user out */
      }
    }

    localStorage.removeItem('simulix_bearer_token');
    commit(UNSET_TOKEN_DATA);
    dispatch('redirectToLoginPage');
  },
  async refreshAccessToken({ getters, commit, dispatch }) {
    if (!getters.isAccessTokenRefreshable) return;

    try {
      const response = await axios.put('auth', { refresh_token: getters.user.refreshToken });
      const token = response.data.access_token;
      localStorage.setItem('simulix_bearer_token', token);
      commit(SET_TOKEN_DATA, token);
      dispatch('redirectToDefaultPage');
    } catch (err) {
      localStorage.removeItem('simulix_bearer_token');
      commit(UNSET_TOKEN_DATA);
      dispatch('redirectToLoginPage');
    }
  },
  setTempToken({ commit }, tokenOrNull) {
    commit(SET_TEMP_TOKEN, tokenOrNull);
  },
  redirectToLoginPage() {
    if (!router.history.current.meta.publicRoute) {
      router.push({ name: 'Login', query: router.history.current.query });
    }
  },
  redirectToDefaultPage({ getters }) {
    const isAdminRoute = router.history.current.matched.some(
      (match) => match.parent && match.parent.name === 'AdminWrapper'
    );

    if (router.history.current.name === 'OAuthSilentSignin') {
      router.replace(router.history.current.query.next || { name: 'ApplicationSelector' });
    }

    if (router.history.current.meta.publicRoute || (isAdminRoute && !getters.isUserAdmin)) {
      router.push(router.history.current.query.next || { name: 'ApplicationSelector' });
    }
  },
};

/* eslint-disable no-param-reassign */
const mutations = {
  [SET_TOKEN_DATA](state, token) {
    state.accessToken = token;
  },
  [UNSET_TOKEN_DATA](state) {
    state.accessToken = null;
  },
  [SET_TEMP_TOKEN](state, tokenOrNull) {
    state.tempToken = tokenOrNull;
  },
};
/* eslint-enable no-param-reassign */

export default {
  state: initialState,
  getters: authGetters,
  actions,
  mutations,
};
